import { TeamsActions } from "@gtmhub/teams/redux/teams-actions";
import { IStateInit } from "@gtmhub/core/routing";
import { IRootScopeService, IQService, IPromise, IScope } from "angular";
import { StateService } from "@uirouter/angularjs";
import { TeamService, ITeam, Paginator, ITeamsStoreState } from "@gtmhub/teams";
import { getCurrentUserId } from "@gtmhub/users";
import { AccountResolverService } from "@gtmhub/state/accountResolverService";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { EmployeeService, IEmployee } from "@gtmhub/employees";
import { IRole, RoleService } from "@gtmhub/roles";
import { IRestLayerRequest } from "@gtmhub/core";
import { INgRedux } from "@gtmhub/state-management";
import { IdMap } from "@gtmhub/util";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";

type ActiveTab = "teams" | "employees" | "quickFilters";

interface IOwnerSelectorParams {
  selectionType: string;
  ownerIds: string;
  teamAndMemberIds: string;
  principalIds: string;
  planningSessionId: string;
}

export interface IOwnerSelectorScope extends IScope, IExtendedRootScopeService, IOwnerSelectorRedux {
  indicators: {
    loading: boolean;
    search: boolean;
  };
  paginator: Paginator;
  ownerIds: string[];
  teamAndMemberIds: string[];
  params: IOwnerSelectorParams;
  userId: string;
  employees: IEmployee[];
  roles: IRole[];
  teams: ITeam[];
  accountId: string;
  activeTab: ActiveTab;
  querySearch: string;

  toggle(id: string): void;
  toggleTeamAndMembers(id: string): void;
  apply(): void;
  setAutofocus(): void;

  onPageChange(page: number, type: "employees" | "teams"): void;
  onTabChange(tab: ActiveTab): void;

  getTeams(querySearch: string, tab: ActiveTab): void;
  search(querySearch: string, type: "employees" | "teams"): void;
}
interface IOwnerSelectorRedux {
  teamsIdMap: IdMap<ITeam>;
  teamsInStore: ITeam[];
  teamsFetched: boolean;
  teamsError: IUIError;
}

const bindStateToScope = (state: ITeamsStoreState): IOwnerSelectorRedux => {
  return {
    teamsIdMap: state.teams.map,
    teamsInStore: state.teams.items,
    teamsFetched: state.teams.isFetched,
    teamsError: state.teams.error,
  };
};
export class OwnerSelectorCtrl implements IStateInit {
  private strings = {
    kr: "kr",
    assignee: "assignee",
    permissions: "permissions",
    assignees: "assignees",
    teams: "teams",
    roles: "roles",
    loading: "loading",
    user: "user",
  };

  public static $inject = [
    "$rootScope",
    "$scope",
    "$state",
    "$stateParams",
    "$q",
    "TeamService",
    "RoleService",
    "AccountResolverService",
    "UIErrorHandlingService",
    "EmployeeService",
    "$ngRedux",
    "TeamsActions",
  ];

  constructor(
    private $rootScope: IRootScopeService,
    private $scope: IOwnerSelectorScope,
    private $stateService: StateService,
    private $stateParams: IOwnerSelectorParams,
    private $q: IQService,
    private teamService: TeamService,
    private roleService: RoleService,
    private accountResolverService: AccountResolverService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private employeeService: EmployeeService,
    $ngRedux: INgRedux,
    teamsActions: TeamsActions
  ) {
    const unsubscribe = $ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);
    $ngRedux.dispatch(teamsActions.fetchTeamsIfMissing());
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private init = (): IPromise<unknown> => {
    this.$scope.indicators = {
      loading: false,
      search: false,
    };

    this.$scope.onPageChange = this.onPageChange;
    this.$scope.onTabChange = this.onTabChange;

    this.$scope.getTeams = this.getTeams;
    this.$scope.search = this.search;

    this.setDefaultPaginator();

    if (this.$stateParams.ownerIds) {
      this.$scope.ownerIds = this.$stateParams.ownerIds.split(",");
    } else if (this.$stateParams.principalIds) {
      this.$scope.ownerIds = this.$stateParams.principalIds.split(",");
    } else {
      this.$scope.ownerIds = [];
    }

    if (this.$stateParams.teamAndMemberIds) {
      this.$scope.teamAndMemberIds = this.$stateParams.teamAndMemberIds.split(",");
    } else {
      this.$scope.teamAndMemberIds = [];
    }

    this.$scope.params = { ...this.$stateParams };

    this.$scope.toggle = this.toggle;
    this.$scope.toggleTeamAndMembers = this.toggleTeamAndMembers;
    this.$scope.apply = this.apply;
    this.$scope.setAutofocus = this.setAutofocus;

    this.$scope.userId = getCurrentUserId();
    this.$scope.accountId = this.accountResolverService.getAccountId();
    this.$scope.indicators.loading = true;

    this.getEmployees();

    return this.$q
      .all({
        roles: this.$stateParams.selectionType === this.strings.permissions ? this.roleService.getRoles() : null,
        __: this.$scope.watchUntilAndRejectOnError(
          () => this.$scope.teamsFetched,
          () => this.$scope.teamsError
        ),
      })
      .then(
        ({ roles }) => {
          this.$scope.indicators.loading = false;
          this.$scope.roles = roles?.items || [];

          this.setAutofocus();
        },
        (rejection) => this.uiErrorHandlingService.handleIndicator(rejection, { indicator: "loading" })
      );
  };

  private setDefaultPaginator = () => {
    this.$scope.paginator = {
      currentPage: 1,
      itemsPerPage: 20,
      paginatorLimit: 5,
    };
  };

  private onTabChange = (tab: ActiveTab): void => {
    this.$scope.activeTab = tab;
    this.setDefaultPaginator();

    if (tab === "teams" || tab === "quickFilters") {
      this.getTeams(this.$scope.querySearch, this.$scope.activeTab);
    }

    if (tab === "employees") {
      this.$scope.indicators.search = true;
      // if no name is present at the moment empty string will be passed and no filter will be added to the query
      this.getEmployees(this.$scope.querySearch);
    }
  };

  private search = (querySearch: string, type: "employees" | "teams"): void => {
    this.$scope.querySearch = querySearch;
    this.setDefaultPaginator();

    if (type === "employees") {
      this.$scope.indicators.search = true;
      this.getEmployees(this.$scope.querySearch);
    }

    if (type === "teams") {
      this.getTeams(this.$scope.querySearch, this.$scope.activeTab);
    }
  };

  private getTeams = (searchTeam: string, tab: ActiveTab) => {
    // If there is no searchTeam just show the teams from the store, without making API request
    if (!searchTeam && tab === "teams") {
      const indexFrom = (this.$scope.paginator.currentPage - 1) * this.$scope.paginator.itemsPerPage;
      const indexTo = this.$scope.paginator.currentPage * this.$scope.paginator.itemsPerPage;
      this.$scope.teams = this.$scope.teamsInStore.slice(indexFrom, indexTo).filter((team) => team.isActive !== false);
      this.$scope.paginator.totalPages = Math.ceil(this.$scope.teamsInStore.length / this.$scope.paginator.itemsPerPage);
      this.$scope.paginator.totalCount = this.$scope.teamsInStore.length;
      return this.$q.resolve(); // Just to be consistent with the return type
    }

    this.$scope.indicators.search = true;

    const queryParams: IRestLayerRequest = {
      skip: (this.$scope.paginator.currentPage - 1) * this.$scope.paginator.itemsPerPage,
      limit: this.$scope.paginator.itemsPerPage,
      fields: "id",
    };
    const srchRegexQuery = [{ name: { $regex: searchTeam } }, { description: { $regex: searchTeam } }];
    const teamsQuery = [
      {
        $and: [{ members: { $exists: true } }, { members: { $in: [this.$scope.userId] } }],
      },
      {
        $and: [{ manager: { $exists: true } }, { manager: { $in: [this.$scope.userId] } }],
      },
    ];

    queryParams.filter = {
      $and: [{ isActive: { $ne: false } }],
    };
    const filters = queryParams.filter["$and"] as object[];

    if (searchTeam) {
      filters.push({ $or: srchRegexQuery });
    }

    if (tab === "quickFilters" && !searchTeam) {
      filters.push({ $or: teamsQuery });
    }

    if (tab === "quickFilters" && searchTeam) {
      filters.push({ $or: srchRegexQuery }, { $or: teamsQuery });
    }

    return this.teamService.getTeams(queryParams).then(
      (teamsCollection) => {
        this.$scope.indicators.search = false;
        this.$scope.teams = teamsCollection.items.map((team) => this.$scope.teamsIdMap[team.id]);
        this.$scope.paginator.totalPages = Math.ceil(teamsCollection.totalCount / this.$scope.paginator.itemsPerPage);
        this.$scope.paginator.totalCount = teamsCollection.totalCount;
      },
      (error) => {
        this.uiErrorHandlingService.handleIndicator(error, { indicator: "search", inline: true });
      }
    );
  };

  private getEmployees = (querySearch?: string) => {
    this.$scope.indicators.search = true;

    const queryParams: IRestLayerRequest = {
      skip: (this.$scope.paginator.currentPage - 1) * this.$scope.paginator.itemsPerPage,
      limit: this.$scope.paginator.itemsPerPage,
      fields: "id,firstName,lastName,picture,email,isActive",
    };

    if (querySearch) {
      queryParams.filter = {
        $and: [{ $or: [{ firstName: { $regex: querySearch } }, { lastName: { $regex: querySearch } }] }, { isActive: { $ne: false } }],
      };
    } else {
      queryParams.filter = { isActive: { $ne: false } };
    }

    return this.employeeService.getEmployeesV2(queryParams).then(
      (employeesCollection) => {
        this.$scope.indicators.search = false;
        this.$scope.employees = employeesCollection.items;

        this.$scope.paginator.totalPages = Math.ceil(employeesCollection.totalCount / this.$scope.paginator.itemsPerPage);
      },
      (error) => {
        this.uiErrorHandlingService.handleIndicator(error, { indicator: "search", inline: true });
      }
    );
  };

  private onPageChange = (page: number, type: "employees" | "teams"): void => {
    this.$scope.paginator.currentPage = page;

    if (type === "employees") {
      this.$scope.indicators.search = true;
      this.getEmployees(this.$scope.querySearch);
    }

    if (type === "teams") {
      this.getTeams(this.$scope.querySearch, this.$scope.activeTab);
    }
  };

  private apply = () => {
    switch (this.$stateParams.selectionType) {
      case this.strings.kr:
        this.$stateService.go("^.^", {
          planningSessionId: this.$scope.params.planningSessionId,
          metricOwnerIds: this.$scope.ownerIds.toString(),
          teamAndMemberIds: this.$scope.teamAndMemberIds.toString(),
          page: 1,
        });
        break;
      case this.strings.permissions:
        this.$rootScope.$broadcast("permissionsChosen", this.$scope.ownerIds);
        this.$stateService.go("^.^");
        break;

      default:
        this.$stateService.go("^.^", {
          planningSessionId: this.$scope.params.planningSessionId,
          ownerIds: this.$scope.ownerIds.toString(),
          teamAndMemberIds: this.$scope.teamAndMemberIds.toString(),
          page: 1,
        });
        break;
    }
  };

  private toggle = (id: string) => {
    const index = this.$scope.ownerIds.indexOf(id);
    if (index === -1) {
      this.$scope.ownerIds.push(id);
    } else {
      this.$scope.ownerIds.splice(index, 1);
    }
  };

  private toggleTeamAndMembers = (id: string): void => {
    const index = this.$scope.teamAndMemberIds.indexOf(id);
    if (index === -1) {
      this.$scope.teamAndMemberIds.push(id);
    } else {
      this.$scope.teamAndMemberIds = this.$scope.teamAndMemberIds.filter((teamAndMemberId: string) => teamAndMemberId !== id);
    }
  };

  private setAutofocus = () => {
    this.$scope.$broadcast("focusTextInput");
  };
}
